import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonIcons from "@base/assets/icons";

interface IProps {
  text: string;
}

/**
 * Free text typed in by an admin: kept to three lines with a toggle when it runs longer. Line breaks
 * are kept as typed. Whether the toggle is needed is measured, since it depends on the width.
 */
export default function CollapsibleText({ text }: IProps) {
  //! state
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);

  //! function
  // A different doctor's text starts collapsed again.
  useEffect(() => {
    setIsExpanded(false);
  }, [text]);

  useLayoutEffect(() => {
    const element = ref.current;

    if (!element || isExpanded) {
      return;
    }

    const measure = () => setIsOverflowing(element.scrollHeight > element.clientHeight + 1);
    measure();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, [text, isExpanded]);

  //! render
  return (
    <div className="flex flex-col gap-2">
      <div
        ref={ref}
        className={`whitespace-pre-line break-words text-sm lg:text-base font-['Inter'] text-common-maintext ${isExpanded ? "" : "line-clamp-3"}`}
      >
        {text.trim()}
      </div>

      {isOverflowing || isExpanded ? (
        <button
          type="button"
          onClick={() => setIsExpanded((value) => !value)}
          className="self-center flex items-center gap-1.5 px-3 py-1 text-sm font-medium font-['Inter'] text-common-primary bg-transparent border-0 cursor-pointer"
        >
          {isExpanded ? t("ShowLess") : t("ShowMore")}
          {isExpanded ? <CommonIcons.UpOutlined className="text-xs" /> : <CommonIcons.DownOutlined className="text-xs" />}
        </button>
      ) : null}
    </div>
  );
}
